import React, { createContext, useContext, useState, useEffect } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeColors {
  bg: string;
  bgSecondary: string;
  card: string;
  cardBorder: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  primary: string;
  primaryLight: string;
  headerBg: string;
  headerBorder: string;
  inputBg: string;
  inputBorder: string;
  sidebarBg: string;
  sidebarHover: string;
  rowAlt: string;
}

interface ThemeContextType {
  isDark: boolean;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  colors: ThemeColors;
  /** True while everyone is pinned to dark; hides the theme picker. */
  themeLocked: boolean;
}

const lightColors: ThemeColors = {
  // Matches the mobile app's light palette: warm cream ground, white cards,
  // red primary. The old values were neutral greys with a purple accent, from
  // before the app had a palette at all.
  bg: 'bg-dojo-cream',
  bgSecondary: 'bg-white',
  card: 'bg-white',
  cardBorder: 'border-stone-300',
  text: 'text-stone-900',
  textSecondary: 'text-stone-600',
  textMuted: 'text-stone-400',
  border: 'border-stone-300',
  primary: '#E34234',
  primaryLight: 'bg-dojo-red/10',
  headerBg: 'bg-dojo-cream',
  headerBorder: 'border-stone-300',
  inputBg: 'bg-white',
  inputBorder: 'border-stone-300',
  sidebarBg: 'bg-white',
  sidebarHover: 'hover:bg-stone-100',
  rowAlt: 'bg-stone-50/40',
};

const darkColors: ThemeColors = {
  // The mobile dark palette: #0B1013 ground, #1B2A32 cards. Note these are
  // blue-black rather than neutral black -- the old values used pure black and
  // neutral-900, which reads colder and does not match the app.
  bg: 'bg-dojo-ink',
  bgSecondary: 'bg-dojo-surface',
  card: 'bg-dojo-surface',
  cardBorder: 'border-slate-700',
  text: 'text-slate-100',
  textSecondary: 'text-slate-400',
  textMuted: 'text-slate-500',
  border: 'border-slate-700',
  primary: '#E34234',
  primaryLight: 'bg-dojo-red/20',
  headerBg: 'bg-dojo-surface',
  headerBorder: 'border-slate-700',
  inputBg: 'bg-dojo-surfaceDeep',
  inputBorder: 'border-slate-600',
  sidebarBg: 'bg-dojo-surface',
  sidebarHover: 'hover:bg-dojo-surfaceDeep',
  rowAlt: 'bg-dojo-surfaceDeep/50',
};

const ThemeContext = createContext<ThemeContextType | null>(null);

/**
 * Everyone is on dark, for now.
 *
 * Both palettes are maintained and every new screen is built and checked in
 * both -- this is a lock on what users see, not a decision to drop light mode.
 * Light needs more time than it has had, and shipping a half-tuned theme behind
 * a toggle means some people see the worse one.
 *
 * It overrides the stored preference rather than just changing the default,
 * because anyone who already chose light has 'light' in localStorage and a
 * default would never reach them. The stored value is left untouched, so
 * whatever someone picked before comes back when this is lifted.
 *
 * To lift it: set this to false and restore the Theme block in
 * components/screens/Profile.tsx. Nothing else needs to change.
 */
const FORCE_DARK = true;

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('taskdojo-theme');
    return (saved as ThemeMode) || 'dark';
  });

  const [systemDark, setSystemDark] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    localStorage.setItem('taskdojo-theme', themeMode);
  }, [themeMode]);

  const isDark = FORCE_DARK
    || themeMode === 'dark'
    || (themeMode === 'system' && systemDark);
  const colors = isDark ? darkColors : lightColors;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    document.body.style.backgroundColor = isDark ? '#0B1013' : '#FAF7F0';
    document.body.style.color = isDark ? '#F1F5F9' : '#1C1917';
  }, [isDark]);

  return (
    <ThemeContext.Provider
      value={{ isDark, themeMode, setThemeMode, colors, themeLocked: FORCE_DARK }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
